/**
 * RenderCountTracker — per-frame counter for "how many times did each entity
 * render this frame."
 *
 * Powers the Event Rate debug tool's "RENDERS" section so DJ can answer the
 * core diagnostic question: is the laggy feel from the same entity being
 * drawn twice per frame (a known regression class — predict-path + remote-
 * sync path both touching the same sprite) or from something else.
 *
 * Usage:
 *   - Each renderer call site that mutates a Pixi node calls `note(key)`
 *     with a stable per-entity key (e.g. `player:${sid}`, `mob:${id}`).
 *   - PixiOverlayHost ticker calls `snapshotAndReset()` once per frame.
 *     The returned snapshot is the count per key for the prior frame; the
 *     internal counter is reset for the next.
 *   - The EventRateOverlay reads `lastSnapshot()` to display the values
 *     plus session-level totals.
 *
 * Singleton — no DI needed because there's only ever one Pixi render
 * stack per page.
 */

interface RenderCountState {
  /** Mutable per-frame counts; cleared on snapshot. */
  current: Map<string, number>;
  /** Last completed frame's snapshot. */
  lastFrame: Record<string, number>;
  /** Cumulative count of frames where ANY entity had count > 1. */
  totalDoubleRenderFrames: number;
  /** Cumulative count of (entity, frame) pairs with count > 1. */
  totalDoubleRenderEvents: number;
  /** Frame counter — increments on every snapshotAndReset call. */
  frameCount: number;
}

const state: RenderCountState = {
  current: new Map(),
  lastFrame: {},
  totalDoubleRenderFrames: 0,
  totalDoubleRenderEvents: 0,
  frameCount: 0,
};

export const RenderCountTracker = {
  /**
   * Increment the count for `key` in the current frame. Cheap — single Map
   * read + write. Safe to call from the render hot path.
   */
  note(key: string): void {
    state.current.set(key, (state.current.get(key) ?? 0) + 1);
  },

  /**
   * Roll the current-frame counter into a snapshot and reset. Called once
   * per Pixi ticker iteration. Returns the snapshot for any caller that
   * wants to react immediately to the just-completed frame.
   */
  snapshotAndReset(): Record<string, number> {
    const snapshot: Record<string, number> = {};
    let doubleEvents = 0;
    for (const [k, v] of state.current.entries()) {
      snapshot[k] = v;
      if (v > 1) doubleEvents++;
    }
    state.current.clear();
    state.lastFrame = snapshot;
    state.frameCount++;
    if (doubleEvents > 0) {
      state.totalDoubleRenderFrames++;
      state.totalDoubleRenderEvents += doubleEvents;
    }
    return snapshot;
  },

  /** Return the most recent frame's counts without resetting. */
  lastSnapshot(): Record<string, number> {
    return state.lastFrame;
  },

  /** Lifetime counters for the EventRateOverlay's running summary. */
  getTotals(): {
    frameCount: number;
    totalDoubleRenderFrames: number;
    totalDoubleRenderEvents: number;
  } {
    return {
      frameCount: state.frameCount,
      totalDoubleRenderFrames: state.totalDoubleRenderFrames,
      totalDoubleRenderEvents: state.totalDoubleRenderEvents,
    };
  },

  /** Test-only. Wipes counters between cases so suites don't leak. */
  __resetForTest(): void {
    state.current.clear();
    state.lastFrame = {};
    state.totalDoubleRenderFrames = 0;
    state.totalDoubleRenderEvents = 0;
    state.frameCount = 0;
  },
};
